import type { Language } from '@offer-catalog/services/i18n'
import type { DepartmentWithSubdepartments, SubdepartmentWithFamilies, TreeViewLevel } from '@/types/treeViewTypes'
import clsx from 'clsx'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Families } from '@/components/Families/Families'
import { useFetchFamilies } from '@/hooks/catalog'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, trackAnalyticsEvent } from '@/services/analytics'
import './subdepartments.scss'

export interface DisplayedFamilies {
  subdepartmentId: number
  families: TreeViewLevel[]
}

export function SubdepartmentsContent({
  season,
  department,
}: Readonly<{
  season: string
  department: DepartmentWithSubdepartments
}>) {
  const { i18n, t } = useTranslation()
  const detailPanelRef = useRef<HTMLElement | null>(null)
  const currentLanguage = i18n.language.split('-')[0] as Language
  const [selectedSubdep, setSelectedSubdep]
    = useState<SubdepartmentWithFamilies | null>(null)

  const groupedSubdepart
    = (department?.subdepartments || []).reduce(
      (acc, subdepart) => {
        const label = subdepart.labels?.[currentLanguage]
        if (!label || label.length === 0)
          return acc

        const firstLetter = label[0]
          .normalize('NFD')
          .replace(/[\u0300-\u036F]/g, '')
          .toUpperCase()

        if (!acc[firstLetter])
          acc[firstLetter] = []
        acc[firstLetter].push(subdepart)
        return acc
      },
      {} as Record<string, TreeViewLevel[]>,
    ) || {}

  const sortedLetters = Object.keys(groupedSubdepart).sort((a, b) =>
    a.localeCompare(b),
  )

  const mutationSuccess = (fullSubdepartment: SubdepartmentWithFamilies) => {
    setSelectedSubdep(fullSubdepartment)
  }

  const { fetchFamilies, isPending } = useFetchFamilies()

  const toggleSubdepartment = async (selectedId: number) => {
    const selectedSubdepartment = department.subdepartments.find(
      subdepartment => subdepartment.id === selectedId,
    )

    if (selectedSubdep?.id === selectedId) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_SUBDEPARTMENT_TOGGLED, {
        journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
        season,
        universe_id: department.universe.id,
        department_id: department.id,
        subdepartment_id: selectedId,
        subdepartment_label: selectedSubdepartment?.labels[currentLanguage],
        action: 'deselect',
      })

      setSelectedSubdep(null)
    }
    else {
      const startedAt = performance.now()

      trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_SUBDEPARTMENT_TOGGLED, {
        journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
        season,
        universe_id: department.universe.id,
        department_id: department.id,
        subdepartment_id: selectedId,
        subdepartment_label: selectedSubdepartment?.labels[currentLanguage],
        action: 'select',
      })

      detailPanelRef.current?.focus()

      await fetchFamilies(
        season,
        {
          subdepartmentId: selectedId,
          departmentId: department.id,
          universeId: department.universe.id,
        },
        (fullSubdepartment) => {
          trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_SUBDEPARTMENT_FAMILIES_LOADED, {
            journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
            season,
            universe_id: department.universe.id,
            department_id: department.id,
            subdepartment_id: selectedId,
            subdepartment_label: fullSubdepartment.labels[currentLanguage],
            families_count: fullSubdepartment.families.length,
            load_duration_ms: Math.round(performance.now() - startedAt),
          })
          mutationSuccess(fullSubdepartment)
        },
      )
    }
  }

  return (
    <div
      data-testid="subdepartments-content"
      className="subdepartments-content"
    >
      <div className="subdepartment-scroll">
        <div className="subdepartments-count">
          {department.subdepartments.length}
          {' '}
          {t('subdepartments')}
        </div>
        <div className="subdepartments-list" data-testid="subdepartments-list">
          {sortedLetters.map(letter => (
            <div key={letter} data-testid="subdepartments-sorted">
              <div className="letter" data-testid="letter">
                {letter}
              </div>
              <div>
                {groupedSubdepart[letter]
                  .toSorted((a, b) =>
                    a.labels[currentLanguage].localeCompare(
                      b.labels[currentLanguage],
                    ),
                  )
                  .map(subdepartment => (
                    <button
                      type="button"
                      key={subdepartment.id}
                      data-testid={`subdepartment-label_${subdepartment.id}`}
                      className={clsx('subdepartment-label', {
                        selected: selectedSubdep?.id === subdepartment.id,
                      })}
                      onClick={() => toggleSubdepartment(subdepartment.id)}
                      onKeyDown={event => event.stopPropagation()}
                    >
                      <strong data-testid="subdepartment-label">
                        {subdepartment.labels[currentLanguage]}
                      </strong>
                      {' '}
                      -
                      <span className="subdepartment-label-id">
                        {subdepartment.id}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Families subDepWithFamilies={selectedSubdep} isLoading={isPending} innerRef={detailPanelRef} />
    </div>
  )
}
